/**
 * Picks OpenSeadragon or OpenLayers for a map (docs/0006_osd-viewer-port.md, Viewer selection).
 *
 * Inputs: backend-resolved permissions (`can_use_osd` already folds in the deployment kill switch,
 * the `use_osd_viewer` permission and the organisation ban; superusers always pass), the superuser's
 * own preference, and the features currently in use.
 */
import {Modal} from "antd";
import {store} from "../../../helper/store";
import {setOsdFallbackFeatures} from "../../../redux_actions/gamma.state.action";
import {isOpenLayersMap} from "../utils/map_utils";
import {
    FEATURE_MODES,
    getFeatureForApp,
    getFeatureLabel,
    getFeatureMode,
    isFallbackMode,
} from "./featureRegistry";

export const VIEWERS = {OSD: 'osd', OL: 'ol'};

const OL_READY_POLL_MS = 50;
const OL_READY_MAX_ATTEMPTS = 40;

export const getStateFeatures = (mapState, gammaState) => {
    const slide = mapState?.slide;
    const features = [];
    if (slide?.meta?.takeZStack || mapState?.zStackLevel > 0) features.push('z_stack');
    if (slide?.meta?.exposure_stack?.length > 0) features.push('exposure_stack');
    if (mapState?.bloodMapEnable || gammaState?.bloodMapEnable) features.push('blood_mode');
    if (gammaState?.sync) features.push('split_sync');
    return features;
};

const getOpenAppFeatures = (gammaState) =>
    [gammaState?.topApp, gammaState?.bottomApp].map(getFeatureForApp).filter(Boolean);

/** Returns VIEWERS.OSD / VIEWERS.OL, or null while the viewer configuration is still loading. */
export const resolveViewer = (state, mapState) => {
    const config = state.osdViewerConfigReducer;
    if (!config?.loaded) return null;
    const permissions = config.permissions || {};
    if (!permissions.can_use_osd) return VIEWERS.OL;
    if (permissions.is_superuser && config.user?.preferred_viewer === VIEWERS.OL) return VIEWERS.OL;

    const gammaState = state.gammaStateReducer;
    const activeFeatures = [
        ...getStateFeatures(mapState, gammaState),
        ...getOpenAppFeatures(gammaState),
        ...(gammaState.osdFallbackFeatures || []),
    ];
    if (activeFeatures.some(feature => isFallbackMode(getFeatureMode(feature, config)))) return VIEWERS.OL;
    return VIEWERS.OSD;
};

export const resolveActiveViewer = (state = store.getState()) => {
    const {activeMapId} = state.gammaStateReducer;
    return resolveViewer(state, state.mapsStateReducer[activeMapId]);
};

/** True when `app` should not be offered for the active map's viewer. */
export const isAppHiddenForViewer = (app, viewer, state = store.getState()) => {
    if (viewer !== VIEWERS.OSD) return false;
    const feature = getFeatureForApp(app);
    return !!feature && getFeatureMode(feature, state.osdViewerConfigReducer) === FEATURE_MODES.HIDDEN;
};

const waitForOpenLayersMap = () => new Promise(resolve => {
    const poll = (attempt) => {
        const state = store.getState();
        const slidemap = state.mapsStateReducer[state.gammaStateReducer.activeMapId]?.slidemap;
        if ((isOpenLayersMap(slidemap) && slidemap.getTarget()) || attempt >= OL_READY_MAX_ATTEMPTS) {
            resolve();
            return;
        }
        setTimeout(() => poll(attempt + 1), OL_READY_POLL_MS);
    };
    setTimeout(() => poll(0), 0);
});

const switchToOpenLayersFor = (featureKey) => {
    const current = store.getState().gammaStateReducer.osdFallbackFeatures || [];
    if (!current.includes(featureKey)) store.dispatch(setOsdFallbackFeatures([...current, featureKey]));
    return waitForOpenLayersMap();
};

/**
 * Call before turning on an OSD-registry feature. Resolves true when the caller may proceed
 * (switching the session to OpenLayers first if needed), false when the user cancelled.
 */
export const requestOsdFeature = (featureKey) => new Promise(resolve => {
    const state = store.getState();
    if (resolveActiveViewer(state) !== VIEWERS.OSD) return resolve(true);

    const config = state.osdViewerConfigReducer;
    const mode = getFeatureMode(featureKey, config);
    if (mode === FEATURE_MODES.NATIVE) return resolve(true);
    if (mode === FEATURE_MODES.HIDDEN) return resolve(false);

    const proceedOnOpenLayers = () => switchToOpenLayersFor(featureKey).then(() => resolve(true));
    if (mode === FEATURE_MODES.FALLBACK_AUTO || !config.permissions?.is_superuser) return proceedOnOpenLayers();

    const label = getFeatureLabel(featureKey);
    Modal.confirm({
        title: 'Switch to OpenLayers?',
        content: `${label} needs the OpenLayers (V1) viewer. OpenSeadragon does not support it yet. Switch to OpenLayers to continue?`,
        okText: 'Switch to OpenLayers',
        cancelText: 'Cancel',
        onOk: proceedOnOpenLayers,
        onCancel: () => resolve(false),
    });
});

export const requestOsdApp = (app) => {
    const feature = getFeatureForApp(app);
    return feature ? requestOsdFeature(feature) : Promise.resolve(true);
};
